import type { AppState, Parameters, QualityName } from "./state";
import { PARAMETER_META } from "./state";

export interface UiCallbacks {
  onParameter(key: keyof Parameters, value: number): void;
  onQuality(quality: QualityName): void;
  onPreset(name: string): void;
  onDebug(view: number): void;
  onAudioVolume(value: number): void;
  onAction(action: string): void;
}

export interface UiController {
  update(state: AppState, status: string): void;
  showError(message: string | null): void;
  showHelp(visible?: boolean): void;
  setPanelVisible(visible: boolean): void;
}

function button(label: string, action: string, callbacks: UiCallbacks): HTMLButtonElement {
  const element = document.createElement("button");
  element.type = "button"; element.textContent = label; element.dataset.action = action;
  element.addEventListener("click", () => callbacks.onAction(action));
  return element;
}

export function createUi(root: HTMLElement, state: AppState, callbacks: UiCallbacks): UiController {
  const hud = document.createElement("div"); hud.className = "hud";
  const brand = document.createElement("header"); brand.className = "brand";
  brand.innerHTML = `<div><span class="eyebrow">SCHWARZSCHILD OBSERVATORY</span><h1>GARGANTUA</h1></div>`;
  const topActions = document.createElement("div"); topActions.className = "top-actions";
  topActions.append(button("Controls", "panel", callbacks), button("Help", "help", callbacks), button("Capture", "capture", callbacks));
  brand.append(topActions); hud.append(brand);

  const telemetry = document.createElement("div"); telemetry.className = "telemetry glass";
  const statusLine = document.createElement("div"); statusLine.className = "status-line";
  telemetry.append(statusLine);
  const indicators = document.createElement("div"); indicators.className = "indicators"; telemetry.append(indicators);
  hud.append(telemetry);

  const panel = document.createElement("aside"); panel.className = "control-panel glass";
  const panelHead = document.createElement("div"); panelHead.className = "panel-head";
  panelHead.innerHTML = `<div><span class="eyebrow">FLIGHT & RENDER</span><h2>Parameters</h2></div>`;
  panelHead.append(button("×", "panel", callbacks)); panel.append(panelHead);

  const quality = document.createElement("div"); quality.className = "segmented";
  for (const name of ["standard", "high", "cinematic"] as QualityName[]) {
    const q = document.createElement("button"); q.textContent = name; q.dataset.quality = name;
    q.addEventListener("click", () => callbacks.onQuality(name)); quality.append(q);
  }
  panel.append(quality);

  const presetGrid = document.createElement("div"); presetGrid.className = "preset-grid";
  const presets: readonly (readonly [string, string])[] = [["observer","Observer"],["photon-ring","Photon ring"],["polar","Polar"],["cinematic-wide","Wide"]];
  for (const [name, label] of presets) {
    const p = document.createElement("button"); p.textContent = label; p.dataset.preset = name;
    p.addEventListener("click", () => callbacks.onPreset(name)); presetGrid.append(p);
  }
  panel.append(presetGrid);

  const debugRow = document.createElement("div"); debugRow.className = "debug-row";
  for (let i = 0; i <= 9; i++) {
    const d = document.createElement("button"); d.textContent = String(i); d.dataset.debug = String(i);
    d.addEventListener("click", () => callbacks.onDebug(i)); debugRow.append(d);
  }
  panel.append(debugRow);

  const quickActions = document.createElement("div"); quickActions.className = "quick-grid";
  for (const [label, action] of [["Cinematic", "cinematic"], ["Audio", "audio"], ["Pause", "pause"], ["Fullscreen", "fullscreen"], ["Reset", "reset"]] as const) {
    quickActions.append(button(label, action, callbacks));
  }
  panel.append(quickActions);

  const audioRow = document.createElement("label"); audioRow.className = "audio-row";
  const audioLabel = document.createElement("span"); audioLabel.textContent = "Ambience volume";
  const audioValue = document.createElement("output");
  const audioInput = document.createElement("input"); audioInput.type = "range"; audioInput.min = "0"; audioInput.max = "1"; audioInput.step = "0.01"; audioInput.value = String(state.audioVolume);
  audioInput.addEventListener("input", () => callbacks.onAudioVolume(Number(audioInput.value)));
  audioRow.append(audioLabel, audioValue, audioInput); panel.append(audioRow);

  const controls = document.createElement("div"); controls.className = "sliders";
  const sliderInputs = new Map<keyof Parameters, HTMLInputElement>();
  const valueLabels = new Map<keyof Parameters, HTMLElement>();
  for (const key of Object.keys(PARAMETER_META) as (keyof Parameters)[]) {
    const [label, min, max, step] = PARAMETER_META[key];
    const row = document.createElement("label"); row.className = "slider-row";
    const caption = document.createElement("span"); caption.textContent = label;
    const value = document.createElement("output");
    const input = document.createElement("input"); input.type = "range"; input.min = String(min); input.max = String(max); input.step = String(step); input.value = String(state.parameters[key]);
    input.addEventListener("input", () => callbacks.onParameter(key, Number(input.value)));
    row.append(caption, value, input); controls.append(row); sliderInputs.set(key, input); valueLabels.set(key, value);
  }
  panel.append(controls); hud.append(panel);

  const error = document.createElement("div"); error.className = "error-overlay"; error.hidden = true; hud.append(error);
  const help = document.createElement("div"); help.className = "help-overlay"; help.hidden = true;
  help.innerHTML = `<div class="help-card glass"><span class="eyebrow">KEYBOARD</span><h2>Flight manual</h2><div class="help-grid">
  <kbd>0–9</kbd><span>Debug views</span><kbd>Shift 1–4</kbd><span>Camera presets</span><kbd>C</kbd><span>Cinematic loop</span>
  <kbd>H</kbd><span>HUD</span><kbd>M</kbd><span>Audio</span><kbd>Q</kbd><span>Quality</span><kbd>Space</kbd><span>Pause time</span>
  <kbd>F</kbd><span>Fullscreen</span><kbd>S</kbd><span>Save PNG</span><kbd>R</kbd><span>Reset</span><kbd>?</kbd><span>This help</span></div><button data-close-help>Close</button></div>`;
  help.querySelector("[data-close-help]")?.addEventListener("click", () => { help.hidden = true; }); hud.append(help);
  root.append(hud);

  let panelVisible = !matchMedia("(max-width: 760px)").matches;
  function setPanelVisible(visible: boolean): void { panelVisible = visible; panel.classList.toggle("open", visible); }
  setPanelVisible(panelVisible);

  return {
    update(next, status) {
      hud.classList.toggle("hidden", !next.hudVisible);
      statusLine.textContent = status;
      indicators.innerHTML = `<span>${next.quality.toUpperCase()}</span><span>DEBUG ${next.debugView}</span><span>${next.cinematic ? "CINE" : "ORBIT"}</span><span>${next.paused ? "PAUSED" : "LIVE"}</span><span>${next.audioEnabled ? "AUDIO" : "MUTED"}</span>`;
      panel.querySelectorAll<HTMLButtonElement>("[data-quality]").forEach((el) => el.classList.toggle("active", el.dataset.quality === next.quality));
      panel.querySelectorAll<HTMLButtonElement>("[data-preset]").forEach((el) => el.classList.toggle("active", el.dataset.preset === next.preset));
      panel.querySelectorAll<HTMLButtonElement>("[data-debug]").forEach((el) => el.classList.toggle("active", Number(el.dataset.debug) === next.debugView));
      audioInput.value = String(next.audioVolume); audioValue.textContent = `${Math.round(next.audioVolume * 100)}%`;
      for (const key of Object.keys(PARAMETER_META) as (keyof Parameters)[]) {
        const v = next.parameters[key]; sliderInputs.get(key)!.value = String(v); valueLabels.get(key)!.textContent = Number.isInteger(v) ? String(v) : v.toFixed(v < 0.01 ? 4 : 2);
      }
    },
    showError(message) { error.hidden = !message; error.textContent = message ?? ""; },
    showHelp(visible) { help.hidden = visible === undefined ? !help.hidden : !visible; },
    setPanelVisible(visible) { setPanelVisible(visible); }
  };
}
